import React, { useState, useEffect, useRef } from 'react';
import apiService from '../api/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';

function History() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const historyFetchRef = useRef(false);

  useEffect(() => {
    if (historyFetchRef.current) return;
    historyFetchRef.current = true;

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getUserHistory(50);
        if (data.status === 'success') {
          setHistory(data.history || []);
        } else {
          setError(data.message || 'Failed to fetch history');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const toggleItemExpansion = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setDeleting(itemToDelete.id);
    try {
      const result = await apiService.deleteHistoryItem(itemToDelete.id);
      if (result.status === 'success') {
        setHistory(history.filter(item => item.id !== itemToDelete.id));
        closeDeleteModal();
      } else {
        setError(result.message || 'Failed to delete generation');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete generation');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="content-inner history-container">
          <div className="card-container">
            <h2 className="section-title mb-4">📋 Generation History</h2>
            <div className="flex items-center justify-center py-12">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-wrapper">
        <div className="content-inner history-container">
          <div className="card-container">
            <h2 className="section-title mb-4">📋 Generation History</h2>
            <div className="error-box">
              <p className="error-text">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="content-wrapper">
        <div className="content-inner history-container">
          <div className="card-container">
            <h2 className="section-title mb-4">📋 Generation History</h2>
            <div className="empty-state">
              <div>
                <p className="empty-state-icon">📭</p>
                <p className="empty-state-text">No generation history yet. Start generating to see your history here.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="content-wrapper">
        <div className="content-inner history-container">
          <div className="card-container">
          <h2 className="section-title mb-4">📋 Generation History</h2>
          {error && (
            <div className="error-box">
              <p className="error-text">{error}</p>
            </div>
          )}
          <div className="space-y-3">
            {history.map((item) => {
              const isExpanded = expandedItems[item.id];

              return (
                <div key={item.id} className="card-item mt-4">
                {/* Text and Voice Info - Clickable */}
                <div
                  className="history-item-header"
                  onClick={() => toggleItemExpansion(item.id)}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <p className="item-title">{item.text.substring(0, 100)}{item.text.length > 100 ? '...' : ''}</p>
                    <div className="item-meta-row">
                      <p className="item-meta">
                        <span className="item-meta-label">Voice:</span> {item.voice}
                      </p>
                      {item.temperature !== undefined && (
                        <p className="item-meta">
                          <span className="item-meta-label">Temperature:</span> {item.temperature.toFixed(1)}
                        </p>
                      )}
                      <p className="item-meta">
                        <span className="item-meta-label">Date:</span> {new Date(item.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <span className={`history-expand-icon ${isExpanded ? 'expanded' : ''}`}>
                    ▶
                  </span>
                </div>

                {/* Audio Player and Actions - Only shown when expanded */}
                {isExpanded && (
                  <div className="history-item-footer">
                    {/* Audio Player - Lazy loaded */}
                    {item.audio_url && (
                      <audio
                        controls
                        className="history-audio-player"
                        src={item.audio_url}
                        title={item.file_name}
                      />
                    )}

                    {/* Download and Delete Buttons */}
                    <div className="history-actions">
                      {item.audio_url && (
                        <a
                          href={item.audio_url}
                          download={item.file_name}
                          className="btn-download-history"
                        >
                          Download
                        </a>
                      )}
                      <button
                        onClick={() => openDeleteModal(item)}
                        disabled={deleting === item.id}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
        </div>
      </div>

      {itemToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          title="Delete Generation?"
          message={`Delete "${itemToDelete.text.substring(0, 50)}${itemToDelete.text.length > 50 ? '...' : ''}"?`}
          onConfirm={handleConfirmDelete}
          onCancel={closeDeleteModal}
          isLoading={deleting === itemToDelete.id}
        />
      )}
    </>
  );
}

export default History;
